import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./UserCalendar.css";
import AlertIndicator from "../components/AlertIndicator";
import apiClient from "../api/client";
import { formatDateUTC8 } from "../utils/date";

function UserCalendar({ user }) {
  // 預設日期設為今日
  const [date, setDate] = useState(new Date());
  const [listeningData, setListeningData] = useState({});
  const [ratingsData, setRatingsData] = useState({});
  const [bindDate, setBindDate] = useState(null);
  const [adverseSurveyData, setAdverseSurveyData] = useState({});
  const [showSurveyDialog, setShowSurveyDialog] = useState(false);
  const [selectedDateSurvey, setSelectedDateSurvey] = useState(null);
  const [selectedDateRating, setSelectedDateRating] = useState(null);
  const [selectedDateSong, setSelectedDateSong] = useState("");

  const [allPlaylists, setAllPlaylists] = useState([]);
  const [currentGroupId, setCurrentGroupId] = useState("");
  const [showEditGroupsDialog, setShowEditGroupsDialog] = useState(false);
  const [editSelectedGroupId, setEditSelectedGroupId] = useState("");
  const [isSavingGroup, setIsSavingGroup] = useState(false);

  // 頁面載入時自動查詢今日問卷資料
  useEffect(() => {
    // 同一個 UserCalendar 元件在切換不同使用者時不會重新掛載（路由只是換
    // username 這個 prop），舊使用者還沒回來的請求如果在切換之後才回應，
    // 沒有這個 ignore 旗標會把舊使用者的資料誤蓋到新使用者畫面上。
    let ignore = false;

    // currentGroupId 是元件層級的 state，不會因為切換使用者自動清空；
    // 不重設的話下面 fetchPlaylists 的 `!currentGroupId` 判斷會誤判成
    // 「已經有群組了」而略過新使用者的群組查詢，畫面留著上一位使用者的舊群組。
    setCurrentGroupId("");

    const fetchListeningData = async () => {
      try {
        const res = await apiClient.get(
          `/api/users/${user.username}/listening-history`
        );
        if (ignore) return;
        const formatted = {};
        const ratings = {};
        (res.data || []).forEach((entry) => {
          const minutes = entry.minutes / 60;
          const truncated = Math.floor(minutes * 100) / 100;
          formatted[entry.date] = truncated;
          ratings[entry.date] = entry.rating;
        });
        setListeningData(formatted);
        setRatingsData(ratings);
      } catch (error) {
        console.error("取得聆聽資料時發生錯誤:", error);
      }
    };

    const fetchBindDate = async () => {
      try {
        const res = await apiClient.get(
          `/api/users/${user.username}/bind-date`
        );
        if (ignore) return;
        setBindDate(res.data?.bind_date || null);
      } catch (error) {
        console.error("取得綁定日期失敗:", error);
      }
    };

    const fetchAllPlaylists = async () => {
      try {
        const res = await apiClient.get(`/api/playlists`);
        if (ignore) return;
        setAllPlaylists(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        if (!ignore) setAllPlaylists([]);
      }
    };

    const fetchPlaylists = async () => {
      try {
        const res = await apiClient.get(
          `/api/users/${user.username}/playlists`
        );
        if (ignore) return;
        const list = Array.isArray(res.data) ? res.data : [];
        if (list.length > 0) {
          const current = list.find((p) => p.is_current) || list[0];
          setCurrentGroupId(current.id);
        }
      } catch (err) {}
    };

    // 一次抓取該使用者全部問卷紀錄，讓月曆上每一天都能顯示圖示
    const fetchAllSurveys = async () => {
      try {
        const res = await apiClient.get(
          `/api/users/${user.username}/adverse-surveys`
        );
        if (ignore) return;
        const formatted = {};
        (res.data || []).forEach((survey) => {
          formatted[survey.date_id] = survey;
        });
        setAdverseSurveyData(formatted);
      } catch (error) {
        if (!ignore) setAdverseSurveyData({});
      }
    };

    fetchBindDate();
    fetchListeningData();
    fetchAllPlaylists();
    fetchPlaylists();
    fetchAllSurveys();

    return () => {
      ignore = true;
    };
  }, [user.username]);

  const handleDateClick = (selectedDate) => {
    setDate(selectedDate);
  };

  const formatSurveyDisplay = (survey) => {
    if (!survey) return [];
    const symptoms = [];
    if (survey.q1_yes) symptoms.push(`刺耳感 (嚴重度: ${survey.q1_severity}/5)`);
    if (survey.q2_yes) symptoms.push(`耳朵痛 (嚴重度: ${survey.q2_severity}/5)`);
    if (survey.q3_yes) symptoms.push(`頭痛 (嚴重度: ${survey.q3_severity}/5)`);
    if (survey.q4_yes) {
      symptoms.push(`其他: ${survey.q4_text || "未詳述"} (嚴重度: ${survey.q4_severity}/5)`);
    }
    return symptoms.length > 0 ? symptoms : ["無不良反應"];
  };

  const selectedDateStr = formatDateUTC8(date);
  const minutes = listeningData[selectedDateStr] ?? 0;
  const surveySummary = adverseSurveyData[selectedDateStr];

  const getLevel = (mins) => {
    if (mins < 20) return "danger";
    if (mins < 60) return "warning";
    return "normal";
  };

  const getMessage = (mins) => {
    if (mins === 0) return `${selectedDateStr} 尚無聆聽紀錄`;
    if (mins < 20) return `${selectedDateStr} 聆聽時間為 ${mins} 分鐘，非常低於建議值`;
    if (mins < 60) return `${selectedDateStr} 聆聽時間為 ${mins} 分鐘略於建議值`;
    return `${selectedDateStr} 聆聽時間為 ${mins} 分鐘，狀態正常`;
  };

  const today = new Date();
  const showIndicator = bindDate && new Date(bindDate) <= today;

  const saveUserGroup = async () => {
    if (!editSelectedGroupId) return;
    setIsSavingGroup(true);
    try {
      await apiClient.post(
        `/api/users/${user.username}/groups`,
        { playlist_id: editSelectedGroupId }
      );
      setCurrentGroupId(editSelectedGroupId);
      alert("更新使用者群組成功");
      setShowEditGroupsDialog(false);
    } catch (e) {
      alert("更新使用者群組失敗，請稍後再試");
    } finally {
      setIsSavingGroup(false);
    }
  };

  const currentGroupName =
    allPlaylists.find((p) => p.id === currentGroupId)?.name || currentGroupId || "";

  useEffect(() => {
    // 快速切換日期/使用者時，舊請求可能比新請求晚回來，沒有這個旗標的話
    // 畫面上顯示的評分/歌曲會跟目前選中的日期對不上。
    let ignore = false;

    // 每次選擇日期時查詢該日評分與歌曲
    const fetchRatingAndSong = async () => {
      if (!user.username || !selectedDateStr) return;
      try {
        // 取得評分
        const ratingRes = await apiClient.get(
          `/api/listening-session/${user.username}/${selectedDateStr}/rating`
        );
        if (ignore) return;
        if (ratingRes.data && ratingRes.data.success) {
          setSelectedDateRating(ratingRes.data.rating);
        } else {
          setSelectedDateRating(null);
        }
        // 取得歌曲
        const songRes = await apiClient.get(
          `/api/users/${user.username}/fetchfordatesongs/${selectedDateStr}`
        );
        if (ignore) return;
        if (songRes.data && songRes.data.success && songRes.data.song) {
          setSelectedDateSong(songRes.data.song.title || songRes.data.song.song_id);
        } else {
          setSelectedDateSong("");
        }
      } catch (error) {
        if (!ignore) {
          setSelectedDateRating(null);
          setSelectedDateSong("");
        }
      }
    };
    fetchRatingAndSong();

    return () => {
      ignore = true;
    };
  }, [user.username, selectedDateStr]);

  return (
    <>
      <div className="user-calendar-container">
        <h2 className="user-calendar-title">使用者 {user.username} 的聆聽日曆</h2>

        <div className="user-calendar-group-row">
          <span className="user-calendar-group-label">所屬群組：</span>
          <div className="user-calendar-group-badges">
            {!currentGroupId ? (
              <span className="user-calendar-badge user-calendar-badge-empty">
                尚未指定
              </span>
            ) : (
              <span className="user-calendar-badge">{currentGroupName}</span>
            )}
          </div>
        </div>

        <Calendar
          onChange={handleDateClick}
          value={date}
          className="user-calendar-component"
          tileClassName={({ date, view }) => {
            if (view === "month") {
              const dateStr = formatDateUTC8(date);
              const hasSurvey = adverseSurveyData[dateStr];
              const isRated = ratingsData[dateStr] !== null && ratingsData[dateStr] !== undefined;
              return (
                <div className="calendar-icon-group">
                  {hasSurvey && (
                    <span className="calendar-icon-survey" title="有問卷">📋</span>
                  )}
                  {isRated && (
                    <span className="calendar-icon-rating" title="有評分">💯</span>
                  )}
                </div>
              );
            }
            return null;
          }}
          tileContent={({ date, view }) => {
            if (view === "month") {
              const dateStr = formatDateUTC8(date);
              const hasSurvey = adverseSurveyData[dateStr];
              const isRated = ratingsData[dateStr] !== null && ratingsData[dateStr] !== undefined;
              return (
                <div style={{ position: "relative" }}>
                  {hasSurvey && (
                    <span className="calendar-icon calendar-icon-survey" title="有問卷">📋</span>
                  )}
                  {isRated && (
                    <span className="calendar-icon calendar-icon-rating" title="有評分">💯</span>
                  )}
                </div>
              );
            }
            return null;
          }}
        />

        {showIndicator && (
          <AlertIndicator
            level={getLevel(minutes)}
            message={getMessage(minutes)}
          />
        )}

        <div className="selected-date-info">
          <div className="selected-date-row">
            <span className="selected-date-label">選中日期：</span>
            <span className="selected-date-value">{selectedDateStr}</span>
          </div>
          <div className="selected-date-row">
            <span className="selected-date-label">歌曲：</span>
            <span className="selected-date-value">{selectedDateSong ? selectedDateSong : "無"}</span>
          </div>
          <div className="selected-date-row">
            <span className="selected-date-label">評分：</span>
            <span className={`selected-date-value rating-value${selectedDateRating !== null ? '' : ' empty'}`}>
              {selectedDateRating !== null ? selectedDateRating : "尚未評分"}
            </span>
          </div>
          {surveySummary ? (
            <div className="survey-summary">
              <h4>📋 不良反應問卷摘要</h4>
              <div className="survey-symptoms">
                {formatSurveyDisplay(surveySummary).map((symptom, index) => (
                  <div key={index} className="symptom-item">
                    • {symptom}
                  </div>
                ))}
              </div>
              <button
                className="view-details-btn"
                onClick={() => {
                  setSelectedDateSurvey(surveySummary);
                  setShowSurveyDialog(true);
                }}
              >
                查看詳細內容
              </button>
            </div>
          ) : (
            <div className="no-survey-info">
              <p>📝 該日期沒有不良反應問卷資料</p>
              <small>點擊其他日期查看是否有問卷資料</small>
            </div>
          )}
        </div>
      </div>

      {showSurveyDialog && selectedDateSurvey && (
        <div className="user-calendar-dialog-overlay">
          <div className="user-calendar-dialog-box survey-dialog">
            <h3>📋 不良反應問卷詳情</h3>
            <div className="survey-meta-info">
              <p>
                <strong>日期：</strong>
                {selectedDateSurvey.date_id}
              </p>
              <p>
                <strong>歌曲ID：</strong>
                {selectedDateSurvey.song_id}
              </p>
            </div>
            <div className="survey-details">
              <div className="survey-question">
                <strong>1. 聆聽過程中是否有刺耳感？</strong>
                <div className="answer">
                  <span className={`answer-badge ${selectedDateSurvey.q1_yes ? "yes" : "no"}`}>
                    {selectedDateSurvey.q1_yes ? "是" : "否"}
                  </span>
                  {selectedDateSurvey.q1_yes && (
                    <span className="severity">嚴重度: {selectedDateSurvey.q1_severity}/5</span>
                  )}
                </div>
              </div>
              <div className="survey-question">
                <strong>2. 聆聽過程中是否有耳朵痛？</strong>
                <div className="answer">
                  <span className={`answer-badge ${selectedDateSurvey.q2_yes ? "yes" : "no"}`}>
                    {selectedDateSurvey.q2_yes ? "是" : "否"}
                  </span>
                  {selectedDateSurvey.q2_yes && (
                    <span className="severity">嚴重度: {selectedDateSurvey.q2_severity}/5</span>
                  )}
                </div>
              </div>
              <div className="survey-question">
                <strong>3. 聆聽過程中是否有頭痛？</strong>
                <div className="answer">
                  <span className={`answer-badge ${selectedDateSurvey.q3_yes ? "yes" : "no"}`}>
                    {selectedDateSurvey.q3_yes ? "是" : "否"}
                  </span>
                  {selectedDateSurvey.q3_yes && (
                    <span className="severity">嚴重度: {selectedDateSurvey.q3_severity}/5</span>
                  )}
                </div>
              </div>
              <div className="survey-question">
                <strong>4. 聆聽過程中是否有其他不適？</strong>
                <div className="answer">
                  <span className={`answer-badge ${selectedDateSurvey.q4_yes ? "yes" : "no"}`}>
                    {selectedDateSurvey.q4_yes ? "是" : "否"}
                  </span>
                  {selectedDateSurvey.q4_yes && (
                    <>
                      <div className="other-description">
                        <strong>描述：</strong>
                        {selectedDateSurvey.q4_text || "未詳述"}
                      </div>
                      <span className="severity">嚴重度: {selectedDateSurvey.q4_severity}/5</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="user-calendar-dialog-actions">
              <button className="primary" onClick={() => setShowSurveyDialog(false)}>
                關閉
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditGroupsDialog && (
        <div className="user-calendar-dialog-overlay">
          <div className="user-calendar-dialog-box">
            <h3>編輯所屬群組</h3>
            <div className="user-calendar-checkbox-grid">
              {allPlaylists.length === 0 ? (
                <div style={{ color: "#888" }}>目前沒有可選的群組</div>
              ) : (
                allPlaylists.map((pl) => (
                  <label key={pl.id} className="user-calendar-radio-item">
                    <input
                      type="radio"
                      name="user-group"
                      value={pl.id}
                      checked={editSelectedGroupId === pl.id}
                      onChange={() => setEditSelectedGroupId(pl.id)}
                    />
                    {pl.name}（{pl.id}）
                  </label>
                ))
              )}
            </div>
            <div className="user-calendar-dialog-actions">
              <button
                className="primary"
                disabled={isSavingGroup || !editSelectedGroupId}
                onClick={saveUserGroup}
              >
                {isSavingGroup ? "儲存中..." : "確認"}
              </button>
              <button onClick={() => setShowEditGroupsDialog(false)} disabled={isSavingGroup}>
                取消
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default UserCalendar;
